import { useEffect, useMemo, useState } from 'react';

import { None } from 'components/data/helpers';
import {
  createdDate,
  date,
  description,
  getAssetColumn,
  getInspectionTypeColumn,
  getVendorColumn,
  lastDelivered,
  site,
  status,
} from 'components/data/inspections';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAssetType,
  type AtlasGqlFilterBy,
  type AtlasGqlInspectionListFilter,
  type AtlasGqlReceivedInspectionDelivery,
  AtlasGqlFilterOperator,
  AtlasGqlInspectionStatus,
  useGetInspectionListFiltersQuery,
  useGetInspectionTypesQuery,
  useGetInspectionVendorsQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { StatusTag } from 'utils/color/helpers';
import { useFeatures } from 'utils/features';
import { startCase } from 'utils/format';
import { getDisplayStatus } from 'utils/inspectionStatus';
import { ENHANCED_INSPECTION_STATUSES } from 'utils/release-toggles';

export function useHasInspectionDetailFeature() {
  const { INSPECTION_DETAIL } = useFeatures().features;
  return INSPECTION_DETAIL;
}
function useListFilterModifiedColumns(columns: TTableColumnDef[], hasEnhancedStatuses: boolean) {
  const [columnsWithFilters, setColumnsWithFilters] = useState<TTableColumnDef[]>(columns);
  const { data, error, loading } = useGetInspectionListFiltersQuery();
  const listFilters = data?.inspectionListFilters;

  useEffect(() => {
    if (!loading && !error && listFilters) {
      const columnsWithFilters = columns.map(inspectionColumn => {
        if (!inspectionColumn.dataIndex) {
          return inspectionColumn;
        }

        const filterOptions: (string | AtlasGqlInspectionListFilter)[] | null | undefined = (
          listFilters as any
        )[inspectionColumn.dataIndex];

        const filteredOptions =
          inspectionColumn.dataIndex === 'status' && !hasEnhancedStatuses
            ? filterOptions?.filter(
                v => v === AtlasGqlInspectionStatus.Draft || v === AtlasGqlInspectionStatus.Released
              )
            : filterOptions;

        return {
          ...inspectionColumn,
          filters: filteredOptions?.map(optionValue => {
            // Site and asset filter options come from asset service as objects;
            // inspection filter options come through as plain strings.
            if (typeof optionValue === 'object') {
              // AtlasGqlInspectionListFilterAsset/AtlasGqlInspectionListFilterSite filter option
              return { text: optionValue.name ?? optionValue.id, value: optionValue.id };
            }
            return {
              text:
                inspectionColumn.dataIndex === 'status'
                  ? startCase(optionValue) ?? optionValue
                  : optionValue,
              value: optionValue,
            };
          }),
        };
      });
      setColumnsWithFilters(columnsWithFilters);
    }
  }, [columns, listFilters, error, loading, hasEnhancedStatuses]);

  return columnsWithFilters;
}

export function useServerSideInspectionColumns(
  tableId: string,
  assetIds: string[],
  assetTypes: AtlasGqlAssetType[],
  asset: AtlasGqlAssetType
) {
  const { hasReleaseToggle } = useAccountContext();
  const hasEnhancedStatuses = hasReleaseToggle(ENHANCED_INSPECTION_STATUSES);

  const { data: inspectionTypeData } = useGetInspectionTypesQuery();
  const { data: vendorData } = useGetInspectionVendorsQuery();

  const isSiteDetailsPage = tableId === 'site-inspections-list';

  const statusColumnWithDisplayStatus = useMemo(
    () => ({
      ...status,
      render(_: any, record: any) {
        const rawStatus = record?.status;
        if (!rawStatus) {
          return <None />;
        }
        const displayStatus = getDisplayStatus(rawStatus, hasEnhancedStatuses);
        return (
          <StatusTag
            data-testid="inspection-status"
            status={displayStatus as AtlasGqlInspectionStatus}
          />
        );
      },
    }),
    [hasEnhancedStatuses]
  );

  // Callers pass new `assetIds`/`assetTypes` array refs each render, so key the
  // columns memo on contents — otherwise it recomputes every render and antd's
  // Table infinite-loops ("Maximum update depth exceeded") with a filter open.
  const assetIdsKey = JSON.stringify(assetIds);
  const assetTypesKey = JSON.stringify(assetTypes);

  const columns = useMemo(() => {
    // These columns will appear on fleet page inspection tabs
    if (asset) {
      return [
        date,
        isSiteDetailsPage ? getAssetColumn(tableId, [], []) : undefined,
        getInspectionTypeColumn(inspectionTypeData?.inspectionTypes ?? []),
        description,
        createdDate,
        getVendorColumn(vendorData?.inspectionVendors ?? []),
        statusColumnWithDisplayStatus,
        lastDelivered,
      ].filter((c): c is TTableColumnDef => !!c);
    }

    return [
      date,
      site,
      getAssetColumn(tableId, assetTypes, assetIds),
      getInspectionTypeColumn(inspectionTypeData?.inspectionTypes ?? []),
      createdDate,
      getVendorColumn(vendorData?.inspectionVendors ?? []),
      statusColumnWithDisplayStatus,
      lastDelivered,
    ];
    // assetIds/assetTypes are intentionally keyed by content (assetIdsKey/assetTypesKey) above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableId,
    asset,
    isSiteDetailsPage,
    assetTypesKey,
    assetIdsKey,
    inspectionTypeData,
    vendorData,
    statusColumnWithDisplayStatus,
  ]);

  return useListFilterModifiedColumns(columns, hasEnhancedStatuses);
}

export const generateTableFilters = ({
  siteIds,
  assetIds,
}: {
  siteIds?: string[];
  assetIds?: string[];
}): AtlasGqlFilterBy[] => {
  const initialFilter: AtlasGqlFilterBy[] = [];

  if (siteIds?.length) {
    initialFilter.push({
      key: 'site',
      operator: AtlasGqlFilterOperator.Equals,
      values: siteIds,
    });
  }

  if (assetIds?.length) {
    initialFilter.push({
      key: 'assetOrComponentId',
      operator: AtlasGqlFilterOperator.Equals,
      values: assetIds,
    });
  }

  return initialFilter;
};

export const receivedInspectionDeliveriesLatestCreated = (
  deliveries: AtlasGqlReceivedInspectionDelivery[]
) =>
  deliveries
    .map((d: any) => d.created)
    .filter((c: string): c is string => Boolean(c))
    .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime())[0];
